import { Button, Typography } from '@ag1/ui';
import { viewContentEvent } from 'analytics/events/studio';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { useStoreValue } from 'state/hooks/useStore';
import { useStoreCurrency } from 'state/hooks/useStoreCurrency';
import { firstStepOptionAtom } from '../atoms';
import { ButtonStaticProps, FIRST_STEP_NAME, ProductTypeOption } from '../constants';
import { useBabSanity } from '../hooks';
import { getFirstStepOption, getStoreProductVariantId } from '../utils';
import { styles } from './FirstStep.css';
import { FirstStepInput } from './FirstStepInput/FirstStepInput';
import { useSessionValue } from '@portal/appLayer/auth';

export const FirstStep = () => {
  const router = useRouter();
  const store = useStoreValue();
  const currency = useStoreCurrency();
  const [value] = useAtom(firstStepOptionAtom);
  const { legaleseTextFirstStep, firstVariant, secondVariant, steps, variantOrder } =
    useBabSanity();
    const session = useSessionValue();
  const { firstButtonLink } = steps;

  // Track user-initiated changes vs. initial load
  const isUserAction = useRef(false);
  // Track if we've already fired the initial event
  const hasFiredInitialEvent = useRef(false);

  const handleChange = useCallback(
    async (optionValue: ProductTypeOption, isInitialLoad = false) => {
      // Only mark as user action if it's not initial load
      if (!isInitialLoad) {
        isUserAction.current = true;
      }

      const query = { ...router.query };
      if (optionValue === ProductTypeOption.SECOND_PRODUCT) {
        query.product = 'travel-packs';
      } else {
        delete query.product;
      }

      const selectedVariant =
        optionValue === ProductTypeOption.FIRST_PRODUCT ? firstVariant : secondVariant;

      // Send analytics event for the selected option
      if (selectedVariant) {
        // Get a price value if available
        const price = selectedVariant.singleSubscription?.price
          ? parseFloat(selectedVariant.singleSubscription.price)
          : 0;

        // Get the product ID from the subscription product if available
        let productId = selectedVariant.name;
        let variantId: string | undefined;

        // Try to get a proper product ID from one of the subscription options
        if (selectedVariant.singleSubscription?.ProductIds) {
          variantId = getStoreProductVariantId(
            selectedVariant.singleSubscription.ProductIds,
            store
          );
          if (variantId) {
            productId = variantId;
          }
        } else if (selectedVariant.doubleSubscription?.ProductIds) {
          variantId = getStoreProductVariantId(
            selectedVariant.doubleSubscription.ProductIds,
            store
          );
          if (variantId) {
            productId = variantId;
          }
        }

        // Only update route if it's not initial load
        if (!isInitialLoad) {
          await router.replace(
            {
              pathname: router.pathname,
              query
            },
            undefined,
            { shallow: true }
          );
        }

        // Send the event for both initial load and user actions
        const eventId = isInitialLoad
          ? `first-step-initial-${Date.now()}`
          : `first-step-selection-${Date.now()}`;
        viewContentEvent({
          contents: [
            {
              variant_id: variantId,
              id: productId,
              title: selectedVariant.name,
              category: optionValue,
              price,
              quantity: 1,
              image_url: selectedVariant.generalImage?.asset?.url || ''
            }
          ],
          eventId,
          currency: currency.currency,
          value: price,
          store,
          user: {
            email: session?.customer.email || ''
          }
        });
      } else if (!isInitialLoad) {
        // If no selected variant and not initial load, just update the route
        await router.replace(
          {
            pathname: router.pathname,
            query
          },
          undefined,
          { shallow: true }
        );
      }
    },
    [router, firstVariant, secondVariant, currency.currency, store, session?.customer.email]
  );

  // Call handleChange on initial load, but only once
  useEffect(() => {
    if (value && !hasFiredInitialEvent.current) {
      hasFiredInitialEvent.current = true;
      handleChange(value, true);
    }
  }, [value, handleChange]);

  const items = useMemo(
    () => [
      {
        ...getFirstStepOption({
          variant: firstVariant,
          type: ProductTypeOption.FIRST_PRODUCT,
          variantOrder
        })
      },
      {
        ...getFirstStepOption({
          variant: secondVariant,
          type: ProductTypeOption.SECOND_PRODUCT,
          variantOrder
        })
      }
    ],
    [firstVariant, secondVariant, variantOrder]
  );

  return (
    <>
      <div className={styles.cards}>
        {items.map((option) => (
          <FirstStepInput
            key={option.singleSubscription.price}
            id={option.value}
            name={FIRST_STEP_NAME}
            type="radio"
            value={option.value}
            defaultChecked={option.value === value}
            checked={option.value === value}
            onChange={() => handleChange(option.value)}
            title={option.name}
            description={option.description}
            price={option.minPrice}
            img={option.generalImage}
          />
        ))}
      </div>
      <Button
        {...ButtonStaticProps}
        theme="new"
        size={{ xxs: 'medium', s: 'large' }}
        variant={firstButtonLink?.variant || 'primary'}
        icon={firstButtonLink.showIcon && firstButtonLink.iconOptions?.icon}
        iconPosition={firstButtonLink.iconOptions?.position}>
        {firstButtonLink.portableText && (
          <CustomPortableText value={firstButtonLink.portableText} />
        )}
      </Button>
      {legaleseTextFirstStep && (
        <div className={styles.legaleseTextContainer}>
          {legaleseTextFirstStep.map((text) => (
            <Typography
              key={text}
              noPadding
              size="3"
              variant="body"
              className={styles.legaleseText}>
              {text}
            </Typography>
          ))}
        </div>
      )}
    </>
  );
};
